/**
 * ========================
 * BOOKINGS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Booking Management domain (mounted at
 * `/app/bookings/*`). Modelled on the HR Management menu shape — section
 * visibility is the gate, individual items inherit from there.
 *
 * Sections:
 *   1. My Workspace          — at-a-glance views (Dashboard, Calendar)
 *   2. My Records            — personal booking records (My Bookings, My Licence)
 *   3. Quick Actions         — primary CTA (New Booking)
 *   4. Booking Operations    — admin only (Overview, All Bookings, Resources)
 *   5. Asset Management      — admin only (RFID Enrolment, Tracked Assets, Inventory Audit)
 *   6. Booking Administration — admin only (Driver Profiles, Expiry Calendar, Lookups, Settings, Reports)
 *
 * Live count badges read from `useBookingBadgesStore` synchronously via
 * `getState()` — see `frontend/ccms_v2/src/domain/badges/bookingBadgesStore.ts`.
 */

import {
  IconHome,
  IconCalendar,
  IconClipboardList,
  IconUserCircle,
  IconPlus,
  IconLayoutDashboard,
  IconList,
  IconBox,
  IconShieldCheck,
  IconGps,
  IconClipboardCheck,
  IconLicense,
  IconCalendarStats,
  IconDatabase,
  IconSettings,
  IconReportAnalytics,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck, anyVisibilityCheck } from './BaseMenuProvider';
import { useBookingBadgesStore } from '@/domain/badges/bookingBadgesStore';

const BOOKING_ADMIN_ROLES = ['BookingAdmin', 'Admin'];

class BookingsMenuProvider implements DomainMenuProvider {
  domainId = 'bookings';

  getMenuConfig(): DomainMenuConfig {
    const allUsers = createVisibilityCheck();
    const bookingAdmin = anyVisibilityCheck({ roles: BOOKING_ADMIN_ROLES });

    const sections: MenuSection[] = [
      // ---------------- staff (everyone) ----------------
      {
        id: 'bookings-my-workspace',
        label: 'My Workspace',
        visible: allUsers,
        items: [
          { id: 'bookings-my-dashboard', label: 'Dashboard', path: '/app/bookings',          icon: <IconHome size={16} />,     visible: allUsers },
          { id: 'bookings-my-calendar',  label: 'Calendar',  path: '/app/bookings/calendar', icon: <IconCalendar size={16} />, visible: allUsers },
        ],
      },
      {
        id: 'bookings-my-records',
        label: 'My Records',
        visible: allUsers,
        items: [
          { id: 'bookings-my-bookings', label: 'My Bookings', path: '/app/bookings/my-bookings', icon: <IconClipboardList size={16} />, visible: allUsers },
          { id: 'bookings-my-licences', label: 'My Licence',  path: '/app/bookings/my-licences', icon: <IconUserCircle size={16} />,   visible: allUsers },
        ],
      },
      {
        id: 'bookings-quick',
        label: 'Quick Actions',
        visible: allUsers,
        items: [
          { id: 'bookings-quick-new', label: 'New Booking', path: '/app/bookings/create', icon: <IconPlus size={16} />, visible: allUsers, badgeColor: 'blue' },
        ],
      },

      // ---------------- BookingAdmin / Admin ----------------
      {
        id: 'bookings-ops',
        label: 'Booking Operations',
        visible: bookingAdmin,
        items: [
          { id: 'bookings-overview',  label: 'Overview',     path: '/app/bookings/admin/overview', icon: <IconLayoutDashboard size={16} />, visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES,
            countSelector: () => useBookingBadgesStore.getState().pendingApprovals || null, badgeColor: 'yellow' },
          { id: 'bookings-all',       label: 'All Bookings', path: '/app/bookings/all',            icon: <IconList size={16} />,            visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES,
            countSelector: () => useBookingBadgesStore.getState().overdue || null, badgeColor: 'red' },
          { id: 'bookings-resources', label: 'Resources',    path: '/app/bookings/resources',      icon: <IconBox size={16} />,             visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
        ],
      },
      {
        id: 'bookings-assets',
        label: 'Asset Management',
        visible: bookingAdmin,
        items: [
          { id: 'bookings-rfid-enrolment', label: 'RFID Enrolment',   path: '/app/bookings/admin/rfid-enrolment',  icon: <IconShieldCheck size={16} />,    visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-tracked',        label: 'Tracked Assets',   path: '/app/bookings/admin/tracked-assets',  icon: <IconGps size={16} />,            visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-inventory',      label: 'Inventory Audit',  path: '/app/bookings/admin/inventory-audit', icon: <IconClipboardCheck size={16} />, visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
        ],
      },
      {
        id: 'bookings-admin',
        label: 'Booking Administration',
        visible: bookingAdmin,
        items: [
          { id: 'bookings-driver-profiles', label: 'Driver Profiles',  path: '/app/bookings/admin/driver-profiles',  icon: <IconLicense size={16} />,          visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-expiry-calendar', label: 'Expiry Calendar',  path: '/app/bookings/admin/expiry-calendar',  icon: <IconCalendarStats size={16} />,    visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-lookups',         label: 'Lookups',          path: '/app/bookings/lookups',                icon: <IconDatabase size={16} />,         visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-settings',        label: 'Settings',         path: '/app/bookings/settings',               icon: <IconSettings size={16} />,         visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
          { id: 'bookings-reports',         label: 'Reports',          path: '/app/bookings/reports',                icon: <IconReportAnalytics size={16} />,  visible: bookingAdmin, requiredRoles: BOOKING_ADMIN_ROLES },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Booking Management — staff self-service + BookingAdmin operations',
      },
    };
  }
}

export const bookingsMenuProvider = new BookingsMenuProvider();
